// AppleMap.jsx - Reusable Apple MapKit JS map wrapper.
// Mimics the subset of the google-map-react contract this app used:
//   center, zoom, onMapReady(map, mapkit), onChange({center, zoom, bounds}),
//   onClick() for background taps.
import React, { useEffect, useRef } from 'react';
import {
  loadMapKit,
  toCoordinate,
  zoomToRegion,
  regionToZoom,
  mapToBounds,
  mapToCenter,
  wasAnnotationTapRecent,
} from '../../services/appleMapKit';

const AppleMap = ({
  center,
  zoom = 14,
  onMapReady,
  onChange,
  onClick,
  showsZoomControl = true,
  style,
  className,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const destroyedRef = useRef(false);

  // Keep latest callbacks without re-creating the map
  const onChangeRef = useRef(onChange);
  const onClickRef = useRef(onClick);
  const onMapReadyRef = useRef(onMapReady);
  onChangeRef.current = onChange;
  onClickRef.current = onClick;
  onMapReadyRef.current = onMapReady;

  const initialCenterRef = useRef(center);
  const initialZoomRef = useRef(zoom);

  // Create / destroy the map
  useEffect(() => {
    destroyedRef.current = false;

    loadMapKit()
      .then((mapkit) => {
        if (destroyedRef.current || !containerRef.current) return;

        const el = containerRef.current;
        const c = initialCenterRef.current || { lat: 13.7563, lng: 100.5018 };

        const map = new mapkit.Map(el, {
          center: toCoordinate(c.lat, c.lng),
          showsMapTypeControl: false,
          showsUserLocationControl: false,
          isRotationEnabled: false,
          showsCompass: mapkit.FeatureVisibility.Hidden,
          showsScale: mapkit.FeatureVisibility.Hidden,
          showsZoomControl,
        });

        // Apply initial zoom via region (span math needs element size)
        try {
          map.region = zoomToRegion(
            c,
            initialZoomRef.current,
            el.clientWidth || 1024,
            el.clientHeight || 768
          );
        } catch (e) {
          console.warn('[AppleMap] could not set initial region:', e);
        }

        // Per-map error surfacing: if tiles fail to load (e.g. Apple rejects
        // the MapKit token), log it so the blank-grid symptom is diagnosable.
        map.addEventListener('error', (event) => {
          console.error('[AppleMap] map error (tiles may render as a blank grid):', event);
        });

        map.addEventListener('region-change-end', () => {
          if (onChangeRef.current) {
            onChangeRef.current({
              center: mapToCenter(map),
              zoom: regionToZoom(map),
              bounds: mapToBounds(map),
            });
          }
        });

        map.addEventListener('single-tap', (event) => {
          // Ignore taps that annotations already consumed
          if (wasAnnotationTapRecent(map)) return;
          if (onClickRef.current) {
            let coord = null;
            try {
              if (event.pointOnPage) {
                const cc = map.convertPointOnPageToCoordinate(
                  new DOMPoint(event.pointOnPage.x, event.pointOnPage.y)
                );
                coord = { lat: cc.latitude, lng: cc.longitude };
              }
            } catch (e) {
              /* coordinate conversion unavailable */
            }
            onClickRef.current(coord);
          }
        });

        mapRef.current = map;
        if (onMapReadyRef.current) onMapReadyRef.current(map, mapkit);
      })
      .catch((err) => {
        console.error('[AppleMap] MapKit failed to initialize:', err);
      });

    return () => {
      destroyedRef.current = true;
      if (mapRef.current) {
        try {
          mapRef.current.destroy();
        } catch (e) {
          /* already destroyed */
        }
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync center prop -> map (external recenter)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !center || typeof center.lat !== 'number') return;
    const current = mapToCenter(map);
    if (
      Math.abs(current.lat - center.lat) > 0.0001 ||
      Math.abs(current.lng - center.lng) > 0.0001
    ) {
      try {
        map.setCenterAnimated(toCoordinate(center.lat, center.lng), true);
      } catch (e) {
        /* map mid-teardown */
      }
    }
  }, [center]);

  return (
    <div
      ref={containerRef}
      className={className}
      // `position: relative; z-index: 0` makes the map its own stacking
      // context, so the very high z-index used to raise an open annotation
      // info box above its neighbours stays inside the map and can never
      // paint over sibling chrome (map control buttons, sheets, modals).
      style={{ width: '100%', height: '100%', position: 'relative', zIndex: 0, ...style }}
    />
  );
};

export default AppleMap;
